import { Link } from "react-router-dom";
import { dataset } from "@/data";
import { pct } from "@/format";

export default function MethodologyPage() {
  const s = dataset.scoring;
  return (
    <div style={{ maxWidth: 820 }}>
      <h1>Methodology &amp; data policy</h1>

      <div className="card card-pad mt">
        <h3>Honesty rules</h3>
        <ul>
          <li>Every displayed value is tagged with its <strong>source(s)</strong>, a <strong>confidence</strong> level, and a verification date.</li>
          <li>Unknown values are shown as <strong>“Unknown”</strong> — never guessed, never defaulted to zero.</li>
          <li>Scores <strong>exclude</strong> unknown metrics rather than penalizing them; each score reports its <strong>coverage</strong> (share of weight backed by real data).</li>
          <li>Shipping prices in the seed dataset are <strong>modeled</strong> (representative structure) and are labeled as such with low confidence. They are for engine demonstration and relative comparison, not quotes.</li>
          <li>Community rumors (e.g. “these agents share one backend”) are only asserted when a citable source exists; otherwise they are recorded as unverified notes.</li>
        </ul>
      </div>

      <div className="card card-pad mt">
        <h3>Deterministic scoring (v{s.version})</h3>
        <p className="small muted">
          Each category score is a weighted average of normalized metrics (0–1 → 0–100). The overall
          score is the weighted average of category scores, over categories that have data. The same
          data always produces the same score — there are no subjective inputs and no randomness.
        </p>
        <div className="table-wrap" style={{ marginTop: 10 }}>
        <table className="data">
          <thead><tr><th>Category</th><th>Overall weight</th><th>Metrics (weight · normalization)</th></tr></thead>
          <tbody>
            {s.categories.map((c) => (
              <tr key={c.id}>
                <td><strong style={{ textTransform: "capitalize" }}>{c.label}</strong><br /><span className="tiny faint">{c.description}</span></td>
                <td className="num">{pct(s.overallWeights[c.id] ?? 0)}</td>
                <td style={{ whiteSpace: "normal", minWidth: 220 }}>
                  {c.metrics.map((m) => (
                    <div key={m.id} className="tiny">
                      <strong>{m.label}</strong> — {pct(m.weight)} · <code>{m.normalize.kind}</code>
                      {"min" in m.normalize ? ` [${m.normalize.min}–${m.normalize.max}${(m.normalize as any).invert ? ", inverted" : ""}]` : ""}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <div className="card card-pad mt">
        <h3>Shipping price engine</h3>
        <p className="small muted">Chargeable weight and price are computed exactly as agents do:</p>
        <pre className="panel" style={{ padding: 12, overflowX: "auto", fontSize: "0.8rem" }}>{`chargeable = max(actualWeight, volumetricWeight, minWeight)
volumetricWeight(g) = (L × W × H cm) / divisor × 1000     // divisor 5000 express, 6000 economy, null = actual-weight lines
price = firstWeightPrice
      + ceil((chargeable − firstWeight) / step) × continuationPrice
price = price × (1 + surchargePct) + surchargeFlat`}</pre>
        <p className="small muted">
          Divisor rules and the first-weight + continuation structure are verified from agents’ own
          fee guides (e.g. Superbuy’s worked example: ¥320 first 500 g + ¥50/500 g + ¥38 customs for
          a HK-UPS tax-free parcel — stored as a real, sourced row). The remaining numeric rate cards
          in the seed are modeled and must be refreshed from live calculators.
        </p>
        <p className="small muted">
          <strong>Verified live quotes.</strong> Where a source exposes real rates, we capture them:
          the Kakobuy → US <Link to="/agent/kakobuy">agent page</Link> lists 12 observed shipping-line
          totals extracted live from JadeShip’s calculator for a fixed, reproducible parcel (marked
          <em> verified</em>, not modeled). This is the same live-extraction path a scheduled importer
          would automate across agents and countries.
        </p>
        <p className="small muted">
          <strong>Why isn’t every cell like that?</strong> This was tested empirically (2026-07-23):
          agents publish per-line/per-country rates through <em>dynamic, JavaScript-driven
          calculators keyed to real-time parcel data</em>, not static tables. Superbuy’s own fee page
          states it provides pricing only via its live calculator and lists no fixed rates for other
          lines; other agents’ static rate pages were unavailable. Collecting “every” verified rate
          therefore requires per-agent live-calculator scraping — the importer contract is built for
          exactly that. We will not fabricate the missing numbers; they stay modeled + labeled until a
          live importer replaces them.
        </p>
      </div>

      <div className="card card-pad mt">
        <h3>Data architecture &amp; refresh</h3>
        <p className="small muted">Normalized JSON in <code>/data</code>, joined at load time:</p>
        <ul className="small">
          <li><code>agents.json</code> — generated by <code>scripts/importers/roster-import.ts</code> from a verified research artifact + hand-curated overrides.</li>
          <li><code>shipping_lines.json</code>, <code>shipping_prices.json</code> (modeled seed), <code>countries.json</code>, <code>features.json</code>, <code>sources.json</code>, <code>scoring_config.json</code>.</li>
          <li><code>npm run validate</code> checks schema, referential integrity and the confidence invariant (unknown ⇔ null).</li>
          <li><code>npm run build:derived</code> regenerates modeled prices and a committed <code>scores.json</code> snapshot.</li>
          <li>Importers are reusable and idempotent, so the database can be refreshed and re-diffed as agents change (new, renamed, discontinued).</li>
        </ul>
      </div>

      <p className="tiny faint mt">Data snapshot: {dataset.meta.generatedAt} · schema v{dataset.meta.schemaVersion}.</p>
    </div>
  );
}
